import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HybrisLayerViewDetails, LdapLayerViewDetails, MssLayerViewDetails, OktaLayerViewDetails, UserDetailsEntity, UserFeatures, UserSecurityRoles } from 'private/app/models/user.model';
import { UserApiService } from 'private/app/services/account-admin/user/user-api.service';

@Component({
    selector: 'hvac-user-details-layer-view',
    templateUrl: './user-details-layer-view.component.html',
    styleUrls: ['./user-details-layer-view.component.scss']
})
export class UserDetailsLayerViewComponent implements OnInit {
    @Input() userDetails: UserDetailsEntity;
    @Output() backtoUserInfo = new EventEmitter();

    public isLoading: boolean;
    public ldapData: LdapLayerViewDetails[];
    public oktaData: OktaLayerViewDetails[];
    public mssData: MssLayerViewDetails[];
    public hybrisData: HybrisLayerViewDetails[];

    public tabs = [
        {
            key: 'OKTA_DATA',
            label: this.translatePipe.instant('USER_ADMIN.LAYER_VIEW.OKTA_DATA')
        },
        {
            key: 'LDAP_DATA',
            label: this.translatePipe.instant('USER_ADMIN.LAYER_VIEW.LDAP_DATA')
        },
        {
            key: 'MSS_DATA',
            label: this.translatePipe.instant('USER_ADMIN.LAYER_VIEW.MSS_DATA')
        },
        {
            key: 'HYBRIS_DATA',
            label: this.translatePipe.instant('USER_ADMIN.LAYER_VIEW.HYBRIS_DATA')
        }
    ];

    public selectedTab = 'OKTA_DATA';

    constructor(
        private userApiService: UserApiService,
        private translatePipe: TranslateService
    ) { }

    ngOnInit(): void {
        this.isLoading = true;
        if (this.userDetails?.userID) {
            this.userApiService.getUseLayerViewDetailsById(this.userDetails.userID).subscribe((data) => {
                this.isLoading = false;
                this.ldapData = data.ldap;
                this.oktaData = data.okta;
                this.hybrisData = data.hybris;
            });
            this.mssData = [this.mapMSSData(this.userDetails)];
        }
    }

    public selectTab(tabKey: string) {
        this.selectedTab = tabKey;
    }

    public backFromLayerView() {
        this.backtoUserInfo.emit(true);
    }

    public mapMSSData(userDetails: UserDetailsEntity): MssLayerViewDetails {
        return {
            id: userDetails.id,
            userID: userDetails.userID,
            hvacPin: userDetails.hvacPin,
            companyId: userDetails.companyId,
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            email: userDetails.email,
            userAddress1: userDetails.userAddress1,
            userAddress2: userDetails.userAddress2,
            userCity: userDetails.userCity,
            userCountry: userDetails.userCountry,
            userState: userDetails.userState,
            userPostalCode: userDetails.userPostalCode,
            userPhone: userDetails.userPhone,
            userFax: userDetails.userFax,
            userCellPhone: userDetails.userCellPhone,
            userStatus: userDetails.userStatus,
            iscbpi: false,
            userPager: userDetails.userPager,
            deactivatedDate: userDetails.deactivatedDate,
            lastLoginDate: userDetails.lastLoginDate,
            phoneExtension: userDetails.userPhone,
            createdBy: userDetails.createdBy ?? '',
            createdDate: userDetails.createdDate ?? '',
            modifiedBy: userDetails.modifiedBy ?? '',
            modifiedDate: userDetails.modifiedDate ?? '',
            hvacCompanyService: [],
            isValidationRequired: userDetails.isValidationRequired,
            lastValidatedDate: userDetails.lastValidatedDate,
            userRolesDetails: userDetails.userRolesDetails.map((role) => this.mapUserRolesDetails(role)),
            additionalFeatures: userDetails.additionalFeatures.map((feature) => this.mapUserFeaturesDetails(feature))
        };
    }

    private mapUserRolesDetails(securityRoles: UserSecurityRoles): UserSecurityRoles {
        return {
            roleId: securityRoles.roleId,
            roleName: securityRoles.roleName,
            approvedBy: securityRoles.approvedBy,
            modifiedBy: securityRoles.modifiedBy,
            modifiedDate: securityRoles.modifiedDate?.replace(/\[.*\]$/, ''),
            roleCategoryId: securityRoles.roleCategoryId,
            roleDisplayName: securityRoles.roleDisplayName,
            roleCode: securityRoles.roleCode
        };
    }

    private mapUserFeaturesDetails(feature: UserFeatures): UserFeatures {
        return {
            featureId: feature.featureId,
            featureName: feature.featureName,
            featureCategoryId: feature.featureCategoryId,
            featureDisplayName: feature.featureDisplayName,
            approvedBy: feature.approvedBy,
            modifiedBy: feature.modifiedBy,
            modifiedDate: feature.modifiedDate?.replace(/\[.*\]$/, '')
        };
    }
}
